import { useEffect, useRef, useState } from "react";

interface StatItem {
  value: number;
  suffix: string;
  prefix?: string;
  label: string;
  icon: string;
}

const stats: StatItem[] = [
  { value: 1200, suffix: "+", label: "Leads Sold", icon: "ri-flashlight-line" },
  { value: 47, suffix: "", label: "Active Installers", icon: "ri-user-star-line" },
  { value: 142, suffix: "", prefix: "$", label: "Avg Lead Price", icon: "ri-price-tag-3-line" },
  { value: 94, suffix: "%", label: "Exclusive Rate", icon: "ri-shield-check-line" },
];

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);

  return count;
}

function StatCounter({ stat, animate }: { stat: StatItem; animate: boolean }) {
  const count = useCountUp(stat.value, 1800, animate);
  return (
    <div className="flex flex-col items-center gap-1 px-6 py-5 flex-1 min-w-[140px]">
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-50 mb-1">
        <i className={`${stat.icon} text-green-600 text-lg`}></i>
      </div>
      <div className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
        {stat.prefix ?? ""}{animate ? count.toLocaleString() : 0}{stat.suffix}
      </div>
      <div className="text-sm text-gray-500 font-medium whitespace-nowrap">{stat.label}</div>
    </div>
  );
}

export default function StatsBar() {
  const ref = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimate(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white border-y border-gray-100">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-stretch justify-center divide-x divide-gray-100">
          {stats.map((stat) => (
            <StatCounter key={stat.label} stat={stat} animate={animate} />
          ))}
        </div>
      </div>
    </section>
  );
}
